import './cart.scss';

import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Icon } from '@eliberty/design-system-react';

import { CartWidgetHTMLElement, pushBeginCheckoutEvent, submitCartCheckout } from '~helpers';
import { useHostElement } from '~providers';
import { selectCartParticipantCount } from '~redux/reducers/cartSlice';
import { CartDrawer } from '~templates';
import type { Simulation } from '~types/appsync.types';
import type { CartProduct } from '~types/cart';

export const CartWidget = () => {
  const intl = useIntl();
  const host = useHostElement<CartWidgetHTMLElement>();

  const [isOpen, setIsOpen] = useState(() => host.hasAttribute('open'));

  const participantCount = useSelector(selectCartParticipantCount);

  useEffect(() => {
    host._controller.setOpen = setIsOpen;
    if (host._pendingOpen !== null) {
      setIsOpen(host._pendingOpen);
      host._pendingOpen = null;
    }
  }, [host]);

  useEffect(() => {
    host._controller.setItemCount(participantCount);
  }, [host, participantCount]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    host.removeAttribute('open');
  }, [host]);

  const handleOpen = useCallback(() => {
    host.setAttribute('open', '');
  }, [host]);

  const handleCheckout = useCallback(
    (products: CartProduct[], simulation: Simulation | undefined) => {
      if (!simulation || products.length === 0) return;
      pushBeginCheckoutEvent({ products, simulation });
      submitCartCheckout(products);
    },
    [],
  );

  const showTrigger = !host.hasAttribute('custom-trigger');
  const displayCount = participantCount > 99 ? '99+' : participantCount;

  return (
    <>
      {showTrigger && (
        <button
          aria-label={intl.formatMessage({ defaultMessage: 'Open cart', id: 'cartAriaOpen' })}
          className='CartWidget_trigger'
          onClick={handleOpen}
          type='button'
        >
          <Icon name='interface-cart-1' />
          {participantCount > 0 && (
            <span aria-hidden className='CartWidget_badge'>
              {displayCount}
            </span>
          )}
        </button>
      )}
      <CartDrawer isOpen={isOpen} onCheckout={handleCheckout} onClose={handleClose} />
    </>
  );
};
